import { useEffect, useRef } from 'react';
import type { PropsWithChildren } from 'react';
import { merkePanelRect } from './panelRect';

interface AccessStageProps {
  /** Kleine Zeile über der Überschrift. */
  readonly eyebrow: string;
  readonly title: string;
  readonly description?: string;
}

/**
 * FORQ Access -- der Zugangsbereich.
 *
 * Kein Fachmodul und kein eigener Dienst, sondern der feste Rahmen um alles, was vor dem
 * Arbeitsplatz liegt: Anmeldung, Passwort vergessen, Zugang anfragen.
 *
 * Der Rahmen ist bewusst still: dunkler Grund, ein feiner Lichtschimmer, die Wortmarke klein.
 * Keine Bildmarke im Hintergrund, keine Sätze über die Plattform. Was hier steht, ist das, was
 * der Nutzer gerade tun soll.
 *
 * Das Panel schwebt -- sehr langsam, 7 Sekunden je Zyklus, ein paar Pixel. Es soll auffallen,
 * dass es schwebt, aber nicht, wie es schwebt. Bei `prefers-reduced-motion` steht es still.
 *
 * Seine Lage wird gemessen und gemerkt (`merkePanelRect`): daraus beginnt nach der Anmeldung der
 * Übergang zur Suchleiste.
 */
export function AccessStage({ eyebrow, title, description, children }: PropsWithChildren<AccessStageProps>) {
  const panel = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = panel.current;
    if (!element) return;

    const messen = () => {
      const rect = element.getBoundingClientRect();
      merkePanelRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    };

    messen();
    // Die Lage ändert sich mit dem Fenster UND mit dem Inhalt (Fehlermeldung, anderer Modus).
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(element);
    window.addEventListener('resize', messen);
    return () => {
      beobachter.disconnect();
      window.removeEventListener('resize', messen);
    };
  }, []);

  return (
    <main className="forq-access">
      <div className="forq-access__atmosphere" aria-hidden="true" />
      <img className="forq-access__wordmark" src="/forq-wordmark.svg" alt="FORQ" width={1340} height={384} />
      <section className="forq-access__panel" ref={panel}>
        <span className="forq-access__eyebrow">{eyebrow}</span>
        <h1 className="forq-access__title">{title}</h1>
        {description ? <p className="forq-access__description">{description}</p> : null}
        {children}
      </section>
    </main>
  );
}
